import { Link } from 'react-router-dom';
import { ExternalLinkIcon } from 'lucide-react';
import { Panel } from '../../ui/Panel';
import { Field } from '../../ui/Field';
import { ImageField } from '../../ui/ImageField';
import { ProductPicker } from '../../ui/ProductPicker';
import { useAdmin } from '../../AdminContext';
import { templateByKey } from '../../marketing/marketingOptions';
import { MAX_PRODUCT_TAGS } from '../socialRules';
import { btnGhost, inputCls } from '../../ui/styles';
import { isFeedable } from '../../../utils/productFeed';
import type { MarketingDesign } from '../../../core/types/marketing';
import type { SocialPost } from '../../../core/types/social';
type Props = {
  post: SocialPost;
  design?: MarketingDesign;
  set: (patch: Partial<SocialPost>) => void;
};

/** Het beeld (ontwerp of eigen foto) en, voor een Instagram-post, de producten om te taggen. */
export function PostImagePanel({
  post,
  design,
  set
}: Props) {
  const {
    marketing,
    catalog
  } = useAdmin();
  const socialDesigns = marketing.designs.filter(d => templateByKey(d.template).channel !== 'Print');
  const groups = [{
    label: 'Posts',
    items: socialDesigns.filter(d => templateByKey(d.template).channel === 'Instagram' && d.template !== 'story')
  }, {
    label: 'Stories',
    items: socialDesigns.filter(d => d.template === 'story')
  }, {
    label: 'Overig',
    items: socialDesigns.filter(d => templateByKey(d.template).channel !== 'Instagram')
  }].filter(g => g.items.length);
  const chooseDesign = (designId: string) => {
    const d = marketing.designs.find(x => x.id === designId);
    const patch: Partial<SocialPost> = {
      designId: designId || undefined
    };
    // Nog geen tekst? Neem de tekst en hashtags van het ontwerp over.
    if (d && !post.caption.trim() && d.caption) patch.caption = d.caption;
    if (d && post.hashtags.length === 0 && d.hashtags.length) patch.hashtags = d.hashtags;
    // Een productontwerp: dat product alvast klaarzetten om te taggen.
    if (d?.productId && !(post.productIds ?? []).length && post.platforms.includes('Instagram-post')) patch.productIds = [d.productId];
    set(patch);
  };
  const canTag = post.platforms.includes('Instagram-post');
  return <Panel title="Beeld" actions={design ? <Link to={`/be/ontwerpen/${design.id}`} className={`${btnGhost} shrink-0`} data-id="element-4469" data-mp="v9tirwir6ivi">
            Ontwerp aanpassen
            <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-4470" data-mp="r8zrqua0knye" />
          </Link> : undefined} data-id="element-4468" data-mp="hb24oa3w4x7d">
      <div className="space-y-5" data-id="element-4471" data-mp="108d5w09320pg">
        <Field label="Ontwerp" hint="Uit Marketing › Ontwerpen. Nog geen tekst? Dan neemt de post die van het ontwerp over." data-id="element-4472" data-mp="10720xx3wimwr">
          <select value={post.designId ?? ''} onChange={e => chooseDesign(e.target.value)} className={inputCls} data-id="element-4473" data-mp="1eyam9u0rtm43">
            <option value="" data-id="element-4474" data-mp="1nwwdseo80bh0">Geen ontwerp — eigen foto</option>
            {groups.map(g => <optgroup key={g.label} label={g.label} data-id="element-4475" data-mp="1iycqoaxw4tn2">
                {g.items.map(d => <option key={d.id} value={d.id} data-id="element-4476" data-mp="22jv0jwc4uhpe">
                    {d.name}
                  </option>)}
              </optgroup>)}
          </select>
        </Field>
        {!design && <Field label="Eigen foto" as="div" hint="Een foto uit de salon werkt vaak beter dan een ontwerp. Staand (4:5) of story (9:16)." data-id="element-4477" data-mp="1tjpz02tmedj5">
            <ImageField value={post.image} onChange={image => set({
          image
        })} fit="cover" aspect="aspect-[4/5] max-w-[220px]" data-id="element-4478" data-mp="1xiyogvhmnp8w" />
          </Field>}
        {!design && !post.image && <p className="text-[13px] text-muted" data-id="element-4479" data-mp="1olocf7wzjw2x">
            Liever een ontwerp?{' '}
            <Link to="/be/ontwerpen/nieuw" className="text-clay underline-offset-4 hover:underline" data-id="element-4480" data-mp="19wierodsqdx5">
              Maak een nieuw ontwerp
            </Link>
          </p>}

        {canTag && <Field label="Producten taggen" as="div" count={{
        value: (post.productIds ?? []).length,
        max: MAX_PRODUCT_TAGS
      }} hint="Voor de Instagram-shop: tag ze bij het plaatsen op de foto, dan tikt ze door naar de webshop. Alleen producten met foto en prijs staan in de shop." data-id="element-4481" data-mp="10vu4n4riwxpj">
            <ProductPicker value={post.productIds ?? []} onChange={productIds => set({
          productIds: productIds.length ? productIds : undefined
        })} products={catalog.products.filter(isFeedable)} emptyText="Geen producten getagd." data-id="element-4482" data-mp="sw5xv6bp14ej" />
          </Field>}
      </div>
    </Panel>;
}